// Leaf — free gifts synced from the offer block.
// The main line carries `_leaf_gifts` = "variantId:minQty|variantId:minQty". Gift lines (hidden €0 products)
// are added/removed here so the cart always matches the selected offer. Gift lines carry `_leaf_gift`.
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION = 'cart-drawer-section';
const SOURCE = 'leaf-gifts';
let busy = false;

const cartJSON = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();

async function mutate(url, body, lines, action) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: SECTION }) }).then(r => r.json()).catch(() => ({}));
  const cart = await cartJSON();
  deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: SOURCE, sections: res.sections, didError: !!res.status } });
  return cart;
}

async function syncGifts() {
  if (busy) return;
  busy = true;
  try {
    const cart = await cartJSON();
    // qty per gift config, then required gift variants
    const required = new Map();
    for (const item of cart.items) {
      const cfg = item.properties && item.properties._leaf_gifts;
      if (!cfg) continue;
      for (const part of String(cfg).split('|')) {
        const [variant, min] = part.split(':').map(Number);
        if (!variant) continue;
        const qty = cart.items.filter(i => i.properties && i.properties._leaf_gifts === cfg).reduce((s, i) => s + i.quantity, 0);
        if (qty >= (min || 1)) required.set(variant, true);
      }
    }
    // bundle extras: a main line with `_leaf_extras` = "v1|v2" needs one line of each extra (same offer label)
    const extrasNeeded = new Map();
    for (const item of cart.items) {
      const ex = item.properties && item.properties._leaf_extras;
      if (!ex) continue;
      for (const v of String(ex).split('|').map(Number).filter(Boolean)) extrasNeeded.set(v, item.properties._leaf_offer || 'Pack');
    }
    const extraLines = cart.items.filter(i => i.properties && i.properties._leaf_bundle_item);
    const extraRemove = extraLines.filter(i => !extrasNeeded.has(i.variant_id));
    const extraPresent = new Set(extraLines.filter(i => extrasNeeded.has(i.variant_id)).map(i => i.variant_id));
    const extraAdd = [...extrasNeeded.keys()].filter(v => !extraPresent.has(v));
    if (extraRemove.length) {
      const updates = {}; extraRemove.forEach(i => { updates[i.key] = 0; });
      await mutate('/cart/update.js', { updates }, extraRemove.map(i => ({ merchandiseId: String(i.variant_id), quantity: 0 })), 'remove');
    }
    if (extraAdd.length) {
      await mutate('/cart/add.js', { items: extraAdd.map(id => ({ id, quantity: 1, properties: { _leaf_bundle_item: extrasNeeded.get(id) } })) }, extraAdd.map(id => ({ merchandiseId: String(id), quantity: 1 })), 'add');
    }
    const giftLines = cart.items.filter(i => i.properties && i.properties._leaf_gift);
    const toRemove = giftLines.filter(i => !required.has(i.variant_id) || i.quantity !== 1);
    const present = new Set(giftLines.filter(i => required.has(i.variant_id) && i.quantity === 1).map(i => i.variant_id));
    const toAdd = [...required.keys()].filter(v => !present.has(v));
    if (toRemove.length) {
      const updates = {}; toRemove.forEach(i => { updates[i.key] = 0; });
      await mutate('/cart/update.js', { updates }, toRemove.map(i => ({ merchandiseId: String(i.variant_id), quantity: 0 })), 'remove');
    }
    if (toAdd.length) {
      await mutate('/cart/add.js', { items: toAdd.map(id => ({ id, quantity: 1, properties: { _leaf_gift: 'Produit Gratuit' } })) }, toAdd.map(id => ({ merchandiseId: String(id), quantity: 1 })), 'add');
    }
  } finally { busy = false; }
}

document.addEventListener(CartLinesUpdateEvent.eventName || 'cart:lines-update', (e) => {
  if (e.detail?.source === SOURCE) return;
  (e.promise || e.detail?.promise || Promise.resolve()).then?.(() => setTimeout(syncGifts, 300));
  setTimeout(syncGifts, 1500);
});
window.addEventListener('pageshow', () => setTimeout(syncGifts, 1000));
